/**
 * Parse a Firebase date object to a Date object
 * @param {any} date Firebase date object
 * @return {Date | null}
 */
export function parseFirebaseDate(date: any): Date | null {
    if (date) {
        return date.toDate()
    }
    return null
}

/**
 * Display a Firebase date object as a string
 * @param date
 */
export function displayFirebaseDate(date: any): string {
    //Check if date is a Date object and convert it to a string
    //else if it is a Firebase date object, convert it to a Date object and then to a string
    if (date instanceof Date) {
        return date.toLocaleString()
    } else if (date) {
        return date.toDate().toLocaleString()
    } else {
        return 'N/A'
    }
}

/**
 * Safely convert a string to a number
 * @param {string} str A string containing a number
 * @return {number | null} The number or null if conversion fails
 */
export function safeConvertToNumber(str: any): number | null | undefined {
    if (str === undefined || str === null) {
        return str
    }
    const num = Number(str)
    return !isNaN(num) ? num : null // Return null if conversion fails
}

/**
 * Deeply compare two objects and return the differences in a format that will be accepted by the Firestore update method
 * Maps with changes will output the specific attributes that have changed only
 * Arrays with changes will output the entire array
 */
export function deepCompare(prevValue: any, newValue: any): Record<string, any> | any {
    const changes: Record<string, any> = {};

    function compareValues(key: string, val1: any, val2: any, path: string) {
        // Handle cases where one or both values are null or undefined
        if (val1 === null || val1 === undefined || val2 === null || val2 === undefined) {
            if (val1 !== val2) {
                changes[path] = val2;
            }
            return;
        }

        // Handle cases where both values are primitives
        if (typeof val1 !== 'object' && typeof val2 !== 'object') {
            if (val1 !== val2) {
                changes[path] = val2;
            }
            return;
        }

        // Handle array comparison
        if (Array.isArray(val1) && Array.isArray(val2)) {
            if (!arraysEqual(val1, val2)) {
                changes[path] = val2;
            }
        } else if (typeof val1 === 'object' && typeof val2 === 'object') {
            // Handle nested object comparison
            const subChanges = deepCompare(val1, val2);
            if (Object.keys(subChanges).length > 0) {
                for (const subKey in subChanges) {
                    changes[`${path}.${subKey}`] = subChanges[subKey];
                }
            }
        } else {
            // One is an object/array, and the other is not
            changes[path] = val2;
        }
    }

    function arraysEqual(arr1: any[], arr2: any[]): boolean {
        if (arr1.length !== arr2.length) {
            return false;
        }
        for (let i = 0; i < arr1.length; i++) {
            if (arr1[i] !== arr2[i]) {
                return false;
            }
        }
        return true;
    }

    // Initial comparison
    if (typeof prevValue === 'object' && typeof newValue === 'object' && prevValue && newValue) {
        for (const key in prevValue) {
            compareValues(key, prevValue[key], newValue[key], key);
        }
        for (const key in newValue) {
            if (!(key in prevValue)) {
                compareValues(key, prevValue[key], newValue[key], key);
            }
        }
    } else if (prevValue !== newValue) {
        // If one of them is a non-object or they're not equal
        return newValue;
    }

    return changes;
}

/**
 * Converts a numbered map (object with numeric keys as strings) to an ordered array.
 *
 * @template T - The type of items stored in the map.
 * @param {Record<string, T>} map - The map with string keys representing numbers and values of type T.
 * @returns {T[]} - An array of values ordered by their numeric keys.
 */
export function numberedMapToArray<T>(map: Record<string, T>): T[] {
  return Object.keys(map)
    .map(key => ({ key: Number(key), value: map[key] })) // Convert keys to numbers and pair with values
    .sort((a, b) => a.key - b.key) // Sort by numeric key order
    .map(item => item.value); // Extract the ordered values into an array
}

/**
 * Converts an array to a map (object) with numbered string keys,
 * maintaining the order of the array.
 *
 * @template T - The type of items stored in the array.
 * @param {T[]} array - The array of items to convert to a map.
 * @returns {Record<string, T>} - A map with keys as stringified indices and values as items from the array.
 */
export function arrayToOrderedMap<T>(array: T[]): Record<string, T> {
  return array.reduce((map: Record<string, T>, item: T, index: number) => {
    map[index.toString()] = item; // Convert index to string for Firestore compatibility
    return map;
  }, {});
}
